import type { LucideIcon } from 'lucide-react';

interface CostPerStageProps {
  title: string;
  icon: LucideIcon;
  iconBgClass: string;
  iconColorClass: string;
  countLabel: string;
  helpText: string;
  periodCpa: number | null;
  periodCount: number;
  lifetimeCpa: number | null;
  lifetimeCount: number;
  loading?: boolean;
}

const formatCurrency = (value: number | null): string => {
  if (value === null) return 'N/A';
  return `$${value.toFixed(2)}`;
};

export function CostPerStage({
  title,
  icon: Icon,
  iconBgClass,
  iconColorClass,
  countLabel,
  helpText,
  periodCpa,
  periodCount,
  lifetimeCpa,
  lifetimeCount,
  loading,
}: CostPerStageProps) {
  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
        <h3 className="text-base font-semibold text-gray-900 mb-3">{title}</h3>
        <div className="space-y-3">
          <div className="h-20 bg-gray-200 rounded animate-pulse" />
          <div className="h-20 bg-gray-200 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm flex flex-col">
      <div className="flex items-center gap-2 mb-3">
        <div className={`p-1.5 rounded-lg ${iconBgClass}`}>
          <Icon className={`w-4 h-4 ${iconColorClass}`} />
        </div>
        <h3 className="text-base font-semibold text-gray-900 truncate">{title}</h3>
      </div>

      <div className="space-y-2 flex-1">
        {/* Selected Period */}
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="text-xs text-gray-500 mb-0.5">Selected Period</div>
          <div className="text-xl font-bold text-gray-900">
            {formatCurrency(periodCpa)}
          </div>
          <div className="text-xs text-gray-500">
            {periodCount.toLocaleString()} {countLabel}
          </div>
        </div>

        {/* Lifetime */}
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="text-xs text-gray-500 mb-0.5">Lifetime</div>
          <div className="text-xl font-bold text-gray-900">
            {formatCurrency(lifetimeCpa)}
          </div>
          <div className="text-xs text-gray-500">
            {lifetimeCount.toLocaleString()} {countLabel}
          </div>
        </div>
      </div>

      <div className="mt-3 text-xs text-gray-500">
        <p>{helpText}</p>
      </div>
    </div>
  );
}
